import axios from 'axios'
import { 
  ImageData, 
  ImageAnnotation, 
  AnnotationUpdate, 
  PaginatedResponse, 
  AnnotationStats,
  ApiResponse,
  ApproverUpdate,
  SpaceType,
  IssueType
} from '../types'

// API Base URL Configuration
// Priority: Environment variable (VITE_API_BASE_URL) > Hardcoded production URL
// For local development, create a .env file with VITE_API_BASE_URL=http://localhost:8000
const apiBaseUrl = import.meta.env?.VITE_API_BASE_URL
const API_BASE_URL =
  typeof apiBaseUrl === 'string' && apiBaseUrl.trim().length > 0
    ? apiBaseUrl.replace(/\/+$/, '')
    : 'https://096tdsev0f.execute-api.us-east-1.amazonaws.com/dev/api'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error)
    return Promise.reject(error)
  }
)

export const imageApi = {
  // Get paginated list of images
  getImages: async (
    page: number = 1,
    limit: number = 50,
    search?: string,
    statusFilter?: string,
    projectFilter?: string,
    space: SpaceType = 'annotator',
    approverStatusFilter?: string
  ): Promise<PaginatedResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      space: space
    })

    if (search) {
      params.append('search', search)
    }

    if (space === 'annotator') {
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter)
      }
    } else if (space === 'approver') {
      if (approverStatusFilter && approverStatusFilter !== 'all') {
        params.append('approver_status_filter', approverStatusFilter)
      }
    }

    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }

    const response = await api.get(`images?${params}`)
    console.log('API Response:', response)
    console.log('API Response Data:', response.data)
    console.log('API Response Data Keys:', Object.keys(response.data || {}))
    console.log('API Response Data.data type:', typeof response.data?.data, Array.isArray(response.data?.data))
    return response.data
  },

  // Get specific image by ID
  getImage: async (imageId: string): Promise<ImageData> => {
    const response = await api.get(`images/${encodeURIComponent(imageId)}`)
    return response.data
  },

  // Create or update annotation
  createAnnotation: async (annotation: ImageAnnotation): Promise<ApiResponse> => {
    const response = await api.post('annotations', annotation)
    return response.data
  },

  // Update existing annotation
  updateAnnotation: async (imageId: string, update: AnnotationUpdate): Promise<ApiResponse> => {
    const response = await api.put(`annotations/${encodeURIComponent(imageId)}`, update)
    return response.data
  },

  // Delete annotation
  deleteAnnotation: async (imageId: string): Promise<ApiResponse> => {
    const response = await api.delete(`annotations/${encodeURIComponent(imageId)}`)
    return response.data
  },

  // Get annotation statistics
  getStats: async (): Promise<AnnotationStats> => {
    const response = await api.get('stats')
    return response.data
  },

  // Get available projects
  getProjects: async (): Promise<string[]> => {
    const response = await api.get('projects')
    return response.data.projects
  },

  // Export annotations as CSV
  exportAnnotations: async (search?: string, statusFilter?: string, projectFilter?: string, space?: string, approverStatusFilter?: string): Promise<Blob> => {
    const params = new URLSearchParams({
      format: 'csv',
    })
    
    if (search) {
      params.append('search', search)
    }
    
    if (space) {
      params.append('space', space)
    }
    
    if (space === 'approver') {
      if (approverStatusFilter && approverStatusFilter !== 'all') {
        params.append('approver_status_filter', approverStatusFilter)
      }
    } else {
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter)
      }
    }
    
    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }
    
    const response = await api.get(`annotations/export?${params}`, {
      responseType: 'blob',
    })
    
    return response.data
  },

  // Download images as ZIP
  downloadImages: async (search?: string, statusFilter?: string, projectFilter?: string, space?: string, approverStatusFilter?: string): Promise<Blob> => {
    const params = new URLSearchParams()
    
    if (search) {
      params.append('search', search)
    }
    
    if (space) {
      params.append('space', space)
    }
    
    if (space === 'approver') {
      if (approverStatusFilter && approverStatusFilter !== 'all') {
        params.append('approver_status_filter', approverStatusFilter)
      }
    } else {
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter)
      }
    }
    
    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }
    
    const response = await api.get(`images/download?${params}`, {
      responseType: 'blob',
    })
    
    return response.data
  },

  // Complete annotations (mark as seen)
  completeAnnotations: async (search?: string, statusFilter?: string, projectFilter?: string): Promise<ApiResponse & { count: number }> => {
    const params = new URLSearchParams()
    
    if (search) {
      params.append('search', search)
    }
    
    if (statusFilter && statusFilter !== 'all') {
      params.append('status_filter', statusFilter)
    }
    
    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }
    
    const response = await api.post(`annotations/complete?${params}`)
    return response.data
  },

  // Update approver status
  updateApproverStatus: async (approverUpdate: ApproverUpdate): Promise<ApiResponse> => {
    const response = await api.post('approver/update', approverUpdate)
    return response.data
  },

  // Complete approver annotations (mark as seen by approver)
  completeApproverAnnotations: async (search?: string, approverStatusFilter?: string, projectFilter?: string): Promise<ApiResponse & { count: number }> => {
    const params = new URLSearchParams()
    
    if (search) {
      params.append('search', search)
    }
    
    if (approverStatusFilter && approverStatusFilter !== 'all') {
      params.append('approver_status_filter', approverStatusFilter)
    }
    
    if (projectFilter && projectFilter !== 'all') {
      params.append('project_filter', projectFilter)
    }
    
    const response = await api.post(`approver/complete?${params}`)
    return response.data
  },

  // Get available issues for annotation
  getAvailableIssues: async (): Promise<{ issues: IssueType[] }> => {
    const response = await api.get('issues')
    return response.data
  },
}

// Utility function to download blob as file
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

export default api 
